"use client";

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";

import { buildFocusHref } from "./HearingsList/build-focus-href";
import {
  encodeFilterState,
  type FilterState,
} from "@/lib/public-hearings/filter-state";
import type { Hearing } from "@/lib/public-hearings/types";
import { HearingDetail } from "./HearingDetail";
import { FocusContext } from "./focus-context";

type Props = {
  list: ReactNode;
  map: ReactNode;
  allHearings: Hearing[];
  filterState: FilterState;
  initialFocusedHearing: Hearing | null;
};

type View = "list" | "map";

export function HearingsListMapLayout({
  list,
  map,
  allHearings,
  filterState,
  initialFocusedHearing,
}: Props) {
  const [view, setView] = useState<View>("list");
  const [hasMountedMap, setHasMountedMap] = useState(false);
  const [focusedHearing, setFocusedHearing] = useState<Hearing | null>(
    initialFocusedHearing,
  );
  // renderedHearing stays set until the close transition finishes so
  // content is visible while the drawer slides out.
  const [renderedHearing, setRenderedHearing] = useState<Hearing | null>(
    initialFocusedHearing,
  );
  const drawerRef = useRef<HTMLDivElement>(null);
  // Element that had focus when the drawer opened — restored on close
  // so keyboard users land back where they were.
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const closeQs = encodeFilterState(filterState).toString();
  const closeHref = closeQs
    ? `/council/public-hearings?${closeQs}`
    : "/council/public-hearings";

  const isOpen = focusedHearing !== null;

  const setFocus = (hearing: Hearing) => {
    setRenderedHearing(hearing);
    setFocusedHearing(hearing);
    window.history.pushState(
      null,
      "",
      buildFocusHref(filterState, hearing.id, hearing.title),
    );
  };

  const clearFocus = useCallback(() => {
    setFocusedHearing(null);
    window.history.pushState(null, "", closeHref);
    // renderedHearing cleared by onTransitionEnd once drawer has closed
  }, [closeHref]);

  // Keep state in sync with browser back/forward navigation. `setFocus`
  // and `clearFocus` use pushState directly (avoiding a full Next.js
  // navigation that would re-fetch the SSR page); the matching popstate
  // listener re-derives the focused hearing from the current URL.
  useEffect(() => {
    const handler = () => {
      const params = new URLSearchParams(window.location.search);
      const slug = params.get("focus");
      if (!slug) {
        setFocusedHearing(null);
        return;
      }
      const id = Number.parseInt(slug.split("-")[0] ?? "", 10);
      if (!Number.isFinite(id)) {
        setFocusedHearing(null);
        return;
      }
      const next = allHearings.find((h) => h.id === id) ?? null;
      if (next) setRenderedHearing(next);
      setFocusedHearing(next);
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [allHearings]);

  // Lock page scroll while drawer is open. Preserve any existing
  // overflow value (e.g. another modal) and restore on close.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Modal focus management: move focus into the drawer on open, trap
  // Tab within it while open, close on Escape, and restore focus to
  // the trigger element on close.
  useEffect(() => {
    if (!isOpen) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const drawer = drawerRef.current;
    if (!drawer) return;
    // Defer to next frame so the drawer's transitioned content is in
    // the DOM and focusable.
    const raf = requestAnimationFrame(() => {
      const first = drawer.querySelector<HTMLElement>(
        'a, button, [tabindex]:not([tabindex="-1"])',
      );
      (first ?? drawer).focus();
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        clearFocus();
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = drawer.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      );
      if (focusables.length === 0) {
        e.preventDefault();
        drawer.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
      restoreFocusRef.current?.focus?.();
    };
  }, [isOpen, clearFocus]);

  const handleTransitionEnd = () => {
    if (!isOpen) setRenderedHearing(null);
  };

  const showMap = () => {
    setHasMountedMap(true);
    setView("map");
  };

  return (
    <FocusContext value={{ focusedHearing, setFocus, clearFocus }}>
      <div className="mt-2">
        {/* View toggle */}
        <div
          className="ph-view-toggle"
          role="group"
          aria-label="Switch between list and map view"
        >
          <span className="ph-filter-glabel">View</span>
          <div className="ph-view-toggle-row">
            <button
              type="button"
              onClick={() => setView("list")}
              aria-pressed={view === "list"}
              className={`ph-filter-chip ${view === "list" ? "ph-filter-chip-pressed" : ""}`}
            >
              List
            </button>
            <button
              type="button"
              onClick={showMap}
              aria-pressed={view === "map"}
              className={`ph-filter-chip ${view === "map" ? "ph-filter-chip-pressed" : ""}`}
            >
              Map
            </button>
          </div>
        </div>

        {/* List — always full width */}
        <div
          className={view === "list" ? "min-w-0" : "ph-view-hidden"}
          aria-hidden={view !== "list"}
        >
          {list}
        </div>

        {/* Map — preserved in DOM once opened */}
        {hasMountedMap && (
          <div
            id="ph-map-panel"
            className={
              view === "map"
                ? "ph-map-panel border border-rule-2 rounded-sm bg-bg overflow-hidden"
                : "ph-view-hidden"
            }
            aria-hidden={view !== "map"}
          >
            {map}
          </div>
        )}
      </div>

      {/* Backdrop — dims page content while drawer is open */}
      <div
        className={`ph-drawer-backdrop${isOpen ? " ph-drawer-backdrop-open" : ""}`}
        aria-hidden="true"
        onClick={clearFocus}
      />

      {/* Drawer — fixed overlay sliding in from the right */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={renderedHearing?.title ?? "Hearing detail"}
        tabIndex={-1}
        className={`ph-drawer${isOpen ? " ph-drawer-open" : ""}`}
        onTransitionEnd={handleTransitionEnd}
      >
        {renderedHearing && (
          <div className="ph-drawer-inner">
            <HearingDetail
              hearing={renderedHearing}
              compact
              closeHref={closeHref}
              onClose={clearFocus}
            />
          </div>
        )}
      </div>
    </FocusContext>
  );
}
